import logger from '@/modules/Logger';
import store from '@/store';
import mainStore from '@/store/index';
import io from 'socket.io-client';
import UAParser from 'ua-parser-js';

import { eventType, lessonResultCode } from '@/utils/constants';
import { initializeTimeSync, receiveTimeSync } from '@/utils/timeSync';
import { getApiMethod } from '@/modules/apiSelector';
import { isCmsPreview } from '@/utils/constants';

export default {
  namespaced: true,
  state: {
    // Lesson.vue
    socket: null,
    socketLessonStart: false,

    // Chatbox/index.vue
    connected: false,
    onConnect: false,
    messageList: [],
    remoteTyping: false,
    remoteSeen: true,
    totalUnseen: 0,

    // VideoButtons.vue
    remoteBrowser: null,
    remoteUserJoined: false,
    remoteMicEnabled: null,
    remoteCameraEnabled: null,
    remoteShareScreen: false,

    initialJoin: true,
    simultaneousConnection: false,
    lessonResultCode: null,
    substituteTrainer: null,
    voluntaryLessonTermination: false,
    // Mirror
    scrollPoint: 0,
    lastEmittedMaterial: {
      isAssessmentLesson: false,
      currentRank: 0,
      currentLesson: 0,
      pdfUrl: ''
    },
    hasNextLessonStatus: null,
    isTimeSyncReceived: false,
    onlineUsers: [],
    slackNotificationCount: 0
  },
  mutations: {
    setSocket(state, payload) {
      state.socket = payload;
    },

    setNewToken(state, payload) {
      state.socket.auth.token = payload;
      state.socket.emit('control', { type: 'refresh-token', token: payload });
    },

    setConnected(state, payload) {
      state.connected = payload;
    },

    pushMessageList(state, payload) {
      state.messageList.push(payload);
    },

    removeMessage(state, payload) {
      state.messageList.forEach(function (i) {
        if (payload.id === i.id) {
          i.m = '';
          return;
        }
      });
    },

    updateTextMessage(state, payload) {
      state.messageList.forEach(function (i) {
        if (payload.id === i.id) {
          i.m = payload.m;
          i.u = true;
          i.wt = payload.wt || [];
          return;
        }
      });
      return;
    },

    insertTrainerTag(state, payload) {
      state.messageList.map(message => {
        const trainerTags = payload.filter(tag => parseInt(tag.message_id) === message.id && !message.e);
        if (trainerTags.length) {
          message['wt'] = trainerTags.map(trainerTag => {
            return {
              id: parseInt(trainerTag.id),
              message_id: parseInt(trainerTag.message_id),
              word: trainerTag.word,
              tags: trainerTag.tags,
              edited: trainerTag.updated_at > trainerTag.created_at,
              updated_at: trainerTag.updated_at
            };
          });
        }
      });
    },

    updateTrainerTag(state, payload) {
      state.messageList.forEach(function (i) {
        const messageTag = parseInt(payload.message_id) === i.id && !i.e;
        if (messageTag) {
          i.wt.forEach(tag => {
            if (parseInt(tag.id) === parseInt(payload.id)) {
              tag.tags = payload.tags;
              tag.edited = true;
              tag.updated_at = payload.updated_at;
            }
          });
          state.socket.emit('message', { type: 'edit-tag', id: parseInt(payload.message_id), wt: i.wt });
        }
      });
    },

    deleteTrainerTag(state, messageId) {
      state.messageList.forEach(function (i) {
        if (messageId === i.id && !i.e) {
          i.wt = [];
        }
      });
    },

    setRemoteSeen(state, payload) {
      state.remoteSeen = payload;
    },

    setSimultaneousConnection(state, payload) {
      state.simultaneousConnection = payload;
    },

    setOmittedMaterial(state, payload) {
      if (!payload.pdfUrl || !payload.isAssessmentLesson) {
        return;
      }

      const lastEmit =
        state.lastEmittedMaterial.currentLesson.toString() + state.lastEmittedMaterial.currentRank.toString();
      const toSendMaterial = payload.currentLesson.toString() + payload.currentRank.toString();
      // force push to submit material emit if we are a trainer
      // to force the student to update the material (on assessment lesson)
      if (toSendMaterial !== lastEmit || payload.isTrainer === true) {
        logger.debug('Send material update to remote if we are a trainer and in an assessment lesson', { 
          operation: 'debug', 
          debug: payload
        });
        state.lastEmittedMaterial = payload;
        state.socket.emit('signal', { type: 'pdf-material', material: payload });
      }
    },

    /**
     * Set Lesson View Event Listener
     * init event on Lesson View component
     *
     * @param {*} state
     */
    setLessonEventListener(state) {
      // Online users
      state.socket.on('online-users', data => {
        state.onlineUsers = data;
      })

      // Socket.io event: Sync time from chat backend server
      state.socket.on('timesync', data => {
        receiveTimeSync(data);
        state.isTimeSyncReceived = true;
      });

      // Default event
      // Socket event that triggered when connection is establish
      state.socket.on('connect', () => {
        logger.info('Established Socket.io connection to Chat backend.', { 
          operation: 'socket:connect', 
          event_type: 82, 
          lesson_id: mainStore.state.lessonId 
        });
        logger.debug('User has joined the room.', { 
          operation: 'socket:connect', 
          event_type: 82, 
          lesson_id: mainStore.state.lessonId 
        });
        state.onConnect = true;
        // Place the time sync initialization here due to not syncing on the first sync request
        initializeTimeSync(state.socket);
        // state.remoteMicEnabled = state.remoteCameraEnabled = true;
      });

      // Socket event that triggered when error happen in while connecting
      state.socket.on('connect_error', (err) => {
        const ua = new UAParser();  
        const conn = navigator.connection || {};
        const transportName = state.socket.io.engine?.transport?.name;

        logger.error('Failed to establish Socket.io connection to Chat backend. Is it running?', {
          operation: 'socket:connect-error', 
          event_type: 80, 
          lesson_id: mainStore.state.lessonId,
          transport: transportName, // shows "polling" or "websocket"
          err
        });

        // If polling fails, switch to websocket-only so the next retry
        // doesn't get stuck on a broken polling transport
        if (transportName === 'polling') {
          logger.warn('Polling transport failed — falling back to websocket-only for retry.', {
            operation: 'socket:transport-fallback',
            lesson_id: mainStore.state.lessonId
          });
          state.socket.io.opts.transports = ['websocket'];
        }

        // Send to Slack (limit to 1 notifications to avoid spamming)
        const slackHookUrl = process.env.VUE_APP_SLACK_HOOK_URL;
        if (slackHookUrl && state.slackNotificationCount < 1) {
          fetch(slackHookUrl, {  
            method: 'POST',  
            body: JSON.stringify({ 
              blocks: [
                {
                  type: "section",
                  text: {
                    type: "mrkdwn",
                    text: "*Socket.io connect_error*"
                  }
                },
                {
                  type: "section",
                  fields: [
                    { type: "mrkdwn", text: `*Lesson ID:*\n${mainStore.state.lessonId}` },
                    { type: "mrkdwn", text: `*User ID:*\n${mainStore.state.userId}` },
                    { type: "mrkdwn", text: `*User Type:*\n${mainStore.state.accType === 't' ? 'Trainer' : 'Student'}` },
                    { type: "mrkdwn", text: `*Provider:*\n${mainStore.state.provider}` },
                    { type: "mrkdwn", text: `*Message:*\n${err.message || err.toString?.() || 'N/A'}` },
                    { type: "mrkdwn", text: `*Description:*\n${err.description ?? 'N/A'}` },
                    { type: "mrkdwn", text: `*Transport:*\n${state.socket.io.engine?.transport?.name || 'N/A'}` },  
                    { type: "mrkdwn", text: `*Browser:*\n${ua.getBrowser().name} ${ua.getBrowser().version}` },  
                    { type: "mrkdwn", text: `*Online:*\n${navigator.onLine}` },  
                    { type: "mrkdwn", text: `*Network:*\n${conn.effectiveType || 'N/A'} (${conn.downlink || '?'} mbps, RTT ${conn.rtt || '?'}ms)` },
                  ]
                }
              ]
            })  
          }).catch(() => {});
          
          // Increment the counter after sending
          state.slackNotificationCount++;
        }
        
        state.connected = false;
        state.onConnect = false;

        // reset initial join upon disconnection on socket.io
        state.initialJoin = true;
      });

      state.socket.on('disconnect', (reason) => {
        logger.warn('Socket.io disconnected from Chat backend.', { 
          operation: 'socket:disconnect', 
          event_type: 83, 
          lesson_id: mainStore.state.lessonId,
          reason
        });
        logger.debug('User has disconnected.', { 
          operation: 'socket:connect', 
          event_type: 83, 
          lesson_id: mainStore.state.lessonId 
        });
        state.connected = false;
        state.onConnect = false;

        // reset initial join upon disconnection on socket.io
        state.initialJoin = true;
      });

      // onControl
      state.socket.on('control', payload => {
        const payloadLog = payload.type === 'push-message-history' ? 'success' : payload;
        logger.info('control: ' + payload.type, { operation: 'socket:control', payload: payloadLog });

        if (store.state.accType === payload.user_type && payload.type !== 'lesson-start') {
          return;
        }

        switch (payload.type) {
          case 'push-message-history':
            // Socket.io event: get history
            if (payload.history.success && payload.history.data.messages !== null) {
              // removed rtc connection event logs
              const filteredHistory = payload.history.data
                .filter(data => {
                  return data.e !== eventType.connectedRtc && data.e !== eventType.disconnectedRtc;
                })
                .map(item => ({ ...item, wt: [] }));
              state.messageList = filteredHistory;
            }
            break;
          case 'connected':
            // Socket.io event: user joined
            break;
          case 'lesson-start':
            // Socket.io event: lesson start
            if (payload.user_type === this.state.accType) {
              state.socketLessonStart = true;
            }
            state.socket.emit('signal', { type: 'mic', state: this.state.devices.micEnabled });
            state.socket.emit('signal', { type: 'camera', state: this.state.devices.cameraEnabled });
            break;
          case 'lesson-not-started':
            // Socket.io event: lesson-not-started
            logger.info('Chat backend responds that lesson is not yet started.');
            break;
          case 'lesson-ended':
            // Socket.io event: lesson-ended
            logger.info(
              'Chat backend responds that lesson has ended (result = ' + payload.lesson_result + ').'
            );
            break;
          case 'lesson-result':
            if (payload.re !== lessonResultCode.NO_RESULT && payload.re !== lessonResultCode.LESSON_TAUGHT) {
              state.lessonResultCode = payload.re;
              state.substituteTrainer = payload.sm ? payload.sm : '';
            }
            logger.info(`Received lesson result: ${payload.re}`);
            break;
          case 'next-lesson-status':
            state.hasNextLessonStatus = payload.hnl;
            break;
          default:
            logger.info('Unspecified payload type', {
              operation: 'socket:control',
              result: 1,
              payload: payload
            });
            break;
        }
      });

      /**
       * onSignal
       * Set VideoChat Component Event Listener
       * init event on video chat component
       */
      state.socket.on('signal', payload => {
        if (payload.type !== 'pilot') {
          logger.info('signal: ' + payload.type, { operation: 'socket:signal', payload: payload });
        }

        // if the incoming signal originates from the same user type as current (local) user, then we are dealing
        // with simultaneous connections of one user from multiple devices
        if (store.state.accType === payload.user_type) {
          if (payload.type === 'user-join') {
            // own user-join event can only be received by the local user once (initial join)
            if (!state.initialJoin) {
              state.simultaneousConnection = true;
              state.socket.close();
            } else {
              state.messageList.push(payload.data);
              state.initialJoin = false;
              // [Chime] On rejoin (e.g. after a network drop) the remote party has reset our device states
              // on 'user-leave', so announce them again
              if (mainStore.getters.isAmazonChime) {
                state.socket.emit('signal', { type: 'mic', state: this.state.devices.micEnabled });
                state.socket.emit('signal', { type: 'camera', state: this.state.devices.cameraEnabled });
                state.socket.emit('signal', { type: 'share-screen', state: this.state.videoRtc.isShareScreenOn });
              }
            }
          }
          return; // user shall never handle signals originating from himself (on another device)
        }

        switch (payload.type) {
          case 'user-join': // a user has joined the lesson room
            // Emit the devices' state to the newly joined remote user
            state.socket.emit('signal', { type: 'mic', state: this.state.devices.micEnabled });
            state.socket.emit('signal', { type: 'camera', state: this.state.devices.cameraEnabled });
            state.socket.emit('signal', { type: 'share-screen', state: this.state.videoRtc.isShareScreenOn });
            state.messageList.push(payload.data);
            state.remoteUserJoined = true;
            break;
          case 'user-leave': // a user has left the lesson room
            state.messageList.push(payload.data);
            // [Chime] After a network drop the server may send the old socket's 'user-leave' after the
            // remote party has already rejoined, which would hide their video. Keep the last known state;
            // a rejoining user re-announces it, and a real leave removes the remote tile anyway.
            if (!mainStore.getters.isAmazonChime) {
              state.remoteMicEnabled = state.remoteCameraEnabled = null;
            }
            state.remoteUserJoined = false;
            // User left the lesson during grace period
            if (this.state.lesson.isLessonWithinGracePeriod) {
              state.voluntaryLessonTermination = true;
            }
            break;
          case 'share-screen': // remote party is starting/stopping screen share
            state.remoteShareScreen = payload.state;
            break;
          case 'mic': // remote party has muted or un-muted his microphone
            // send signal back to counter part to know the state of mic
            if (state.remoteMicEnabled == null) {
              state.socket.emit('signal', { type: 'mic', state: this.state.devices.micEnabled });
            }
            state.remoteMicEnabled = payload.state;
            break;
          case 'camera': // remote party is switching his camera on or off
            // send signal back to counter part to know the state of camera
            if (state.remoteCameraEnabled == null) {
              state.socket.emit('signal', { type: 'camera', state: this.state.devices.cameraEnabled });
            }
            state.remoteCameraEnabled = payload.state;
            break;
          case 'rtc': // rtc system event
            if (payload.connected && payload.user_type && store.state.accType != payload.user_type) {
              state.remoteUserJoined = true;
            }
            break;
          case 'pilot':
            if (store.state.accType === 's') {
              state.scrollPoint = payload.scrollPoint;
            }
            break;
          case 'pdf-material':
            if (store.state.accType === 's' && payload.material) {
              store.state.isAssessmentLesson = payload.material.isAssessmentLesson;
              store.state.material.currentLesson = payload.material.currentLesson;
              store.state.material.currentRank = payload.material.currentRank;
            }
            break;
          case 'remote-browser':
            state.remoteBrowser = payload.browser;
            break;
          default:
            // TODO maybe add error handling here: Socket.io singal type is not recognized
            logger.info('Unspecified payload type', { operation: 'socket:signal', result: 1, payload });
            break;
        }
      });

      /**
       * onMessage
       * Set Chatbox Component Event Listener
       * init event on Chatbox component
       */
      state.socket.on('message', payload => {
        logger.info('message: ' + payload.type, { operation: 'socket:message', payload: payload });

        if (store.state.accType === payload.user_type) {
          return;
        }

        switch (payload.type) {
          // Socket.io event: Get new message
          case 'new':
            state.messageList.push(payload.data);
            state.totalUnseen = state.totalUnseen + 1;
            break;
          // Socket.io event: Get deleted message
          case 'delete':
            var that = state;
            that.messageList.forEach(function (item) {
              if (payload.item.id === item.id) {
                item.m = '';
                item.wt = [];
                return;
              }
            });
            break;
          // Socket.io event: Get notification
          case 'typing':
            // remote typing
            state.remoteTyping = payload.typing;
            break;
          case 'seen':
            // remote seen message
            state.remoteSeen = true;
            break;
          case 'edit':
            state.messageList.forEach(function (item) {
              if (payload.item.id === item.id) {
                item.wt = payload.item.wt;
                item.m = payload.item.m;
                item.u = true;
                return;
              }
            });
            break;
          case 'add-tag':
            state.messageList.forEach(function (item) {
              if (payload.id === item.id && !item.e) {
                item.wt = payload.wt;
              }
            });
            break;
          case 'edit-tag':
            state.messageList.forEach(function (item) {
              if (payload.id === item.id && !item.e) {
                item.wt = payload.wt;
              }
            });
            break;
          default:
            logger.info('Unspecified payload type', {
              operation: 'socket:message',
              result: 1,
              payload: payload
            });
            break;
        }
      });
    },
    setTotalUnseen(state, payload) {
      state.totalUnseen = payload;
    },
    setRemoteUserJoined(state, payload) {
      state.remoteUserJoined = payload;
    }
  },
  getters: {
    /**
     * Check if lesson result is cancelled w/ trainer substitution
     *
     * @return boolean
     */
    hadTrainerSubstitution: state => {
      return state.lessonResultCode === lessonResultCode.TRAINER_SUBSTITUTION;
    },
    /**
     * Check if lesson result is not lesson termination
     *
     * @return boolean
     */
    unterminatedLessonResult: state => {
      return (
        state.lessonResultCode === lessonResultCode.NO_RESULT ||
        state.lessonResultCode === lessonResultCode.LESSON_TAUGHT
      );
    },
    hasNextLesson: state => {
      return state.hasNextLessonStatus;
    }
  },
  actions: {
    /**
     * Initialize socket.io client and listener
     *
     * @param {commit} commit
     * @return void
     */
    initializeSocket({ state, commit }, params) {
      if (isCmsPreview) {
        logger.debug('Socket.IO will not connect on CMS Preview');
        return;
      }
      
      if (state.socket) {
        logger.debug('Socket.IO client already connected - skipping new connection');
        return;
      }
      const host = process.env.VUE_APP_CHAT_HOST;

      const socket = io(host, {
        auth: {
          token: this.state.socketToken || this.state.lessonToken
        },
        query: {
          lesson_id: this.state.lessonId || params.lesson_id
        }
      });

      commit('setSocket', socket);
      commit('setLessonEventListener');
    },

    /**
     * Delete tags attached on delete message
     *
     * @param {commit} commit
     * @param {number} messageId
     * @return void
     */
    async deleteMessageTag({ commit }, messageId) {
      const params = { id: messageId, lesson_id: this.state.lessonId };
      const apiMethod = getApiMethod('deleteTrainerTag');
      await apiMethod(params, () => {
        commit('deleteTrainerTag', messageId);
      });
    }
  }
};
